import { makeStyles } from '@material-ui/core/styles'

import { EphemeralChatSurface } from '../../core/types/chat.types'
import {
  formatAttachmentSizeLimit,
  getAcceptString,
  MAX_ATTACHMENTS_PER_MESSAGE,
} from '../../core/utils/attachment'
import useMaxAttachmentStore, {
  areAttachmentsReadyToSend,
  areAttachmentsUploading,
  hasStagedAttachments,
} from '../../stores/use-max-attachment-store'
import AttachmentStagingArea from './attachment-staging-area'
import AttachmentDropzone from './attachment-dropzone'
import { registerEphemeralSurface, EphemeralSurfaceProps } from './ephemeral-surface-registry'
import UserAvatar from './user-avatar'

const useStyles = makeStyles((theme) => ({
  row: {
    display: 'flex',
    gap: theme.spacing(1.5),
    animation: '$slideIn 180ms ease-out',
  },
  contentHost: {
    flex: 1,
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  card: {
    width: '100%',
    borderRadius: 16,
    border: '1px solid var(--ds-border-default, #d7dae4)',
    background: 'var(--ds-bg-surface, #fff)',
    padding: theme.spacing(2),
    boxShadow: '0 4px 12px rgba(15,23,42,0.08)',
    backdropFilter: 'blur(10px)',
  },
  cardBody: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.5),
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: 4,
  },
  cardTitle: {
    fontSize: '0.875rem',
    fontWeight: 600,
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  cardSubtitle: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  dropZoneArea: {
    borderRadius: 16,
    border: '2px dashed',
    padding: '32px 20px',
    textAlign: 'center',
    transition: 'all 200ms',
    cursor: 'pointer',
  },
  dropZoneDefault: {
    borderColor: 'rgba(232,115,90,0.4)',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    '&:hover': {
      borderColor: 'rgba(232,115,90,0.7)',
      background: 'rgba(232,115,90,0.08)',
    },
  },
  dropZoneActive: {
    borderColor: 'rgba(232,115,90,0.7)',
    background: 'rgba(232,115,90,0.1)',
    boxShadow: '0 0 0 1px rgba(232,115,90,0.08)',
  },
  dropIconHost: {
    margin: '0 auto 12px',
    width: 40,
    height: 40,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: '50%',
    background: 'var(--ds-bg-surface, #fff)',
  },
  dropText: {
    fontSize: '0.875rem',
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: '0 0 4px',
  },
  dropLink: {
    fontWeight: 600,
    color: 'var(--color-coral-sender, #E8735A)',
    cursor: 'pointer',
    background: 'none',
    border: 0,
    padding: 0,
    fontFamily: 'inherit',
    fontSize: 'inherit',
  },
  dropHint: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: '4px 0 0',
  },
  footer: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12,
    paddingTop: 4,
  },
  statusText: {
    fontSize: '0.75rem',
    color: 'var(--ds-fg-tertiary, #8c92a5)',
    margin: 0,
  },
  submitButton: {
    borderRadius: 12,
    padding: '8px 16px',
    fontSize: '0.875rem',
    fontWeight: 600,
    color: '#fff',
    border: 0,
    transition: 'all 200ms',
  },
  submitEnabled: {
    background: 'linear-gradient(to right, #f47d6b, #f68778)',
    cursor: 'pointer',
    '&:hover': {
      boxShadow: '0 0 20px rgba(232,115,90,0.4)',
    },
  },
  submitDisabled: {
    cursor: 'not-allowed',
    background: 'var(--ds-bg-subtle, #f1f2f7)',
    color: 'var(--ds-fg-disabled, #9aa1b4)',
  },
  infoBox: {
    marginTop: theme.spacing(2),
    width: '100%',
    borderRadius: 16,
    border: '1px solid rgba(232,115,90,0.6)',
    background: 'rgba(232,115,90,0.05)',
    padding: '12px 16px',
  },
  infoRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    fontSize: '0.875rem',
  },
  infoIcon: {
    width: 24,
    height: 24,
    flexShrink: 0,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    background: 'var(--ds-bg-surface, #fff)',
    color: 'var(--ds-fg-secondary, #535b6c)',
    fontWeight: 700,
    fontSize: '0.75rem',
  },
  infoText: {
    color: 'var(--ds-fg-primary, #1f2430)',
    margin: 0,
  },
  '@keyframes slideIn': {
    from: { opacity: 0, transform: 'translateY(6px)' },
    to: { opacity: 1, transform: 'translateY(0)' },
  },
}))

const AnalyzeUploadSurface = ({ onSubmit, onAttachFiles }: EphemeralSurfaceProps) => {
  const classes = useStyles()

  const attachments = useMaxAttachmentStore((state) => state.attachments)
  const attachmentError = useMaxAttachmentStore((state) => state.error)
  const removeAttachment = useMaxAttachmentStore((state) => state.removeAttachment)

  const hasAttachments = hasStagedAttachments(attachments)
  const isReadyToSend = areAttachmentsReadyToSend(attachments)
  const isUploading = areAttachmentsUploading(attachments)

  const canSubmit = hasAttachments && isReadyToSend && !isUploading

  const uploadStatusText = (() => {
    if (isUploading) return 'Uploading attachments. Submit becomes available when all files are ready.'
    if (attachmentError) return attachmentError
    if (!hasAttachments) return 'No files added yet.'
    return 'Files are ready to send.'
  })()

  const handleFilesSelected = (files: File[]) => {
    onAttachFiles(files)
  }

  const handleSubmit = () => {
    if (!canSubmit) return
    // onSubmit is called with the surface; parent handles actually sending
    onSubmit({ kind: 'analyze-upload-form' } as EphemeralChatSurface)
  }

  return (
    <div className={classes.row}>
      <UserAvatar type="assistant" />
      <div className={classes.contentHost}>
        <div className={classes.card}>
          <div className={classes.cardBody}>
            <div className={classes.header}>
              <p className={classes.cardTitle}>Upload Documents</p>
              <p className={classes.cardSubtitle}>
                Add the requested files to continue the Analyze flow. This form is temporary and
                will disappear after you submit.
              </p>
            </div>

            <AttachmentDropzone
              accept={getAcceptString()}
              onFilesSelected={handleFilesSelected}
            >
              {({ isDragOver, openFilePicker }) => (
                <div
                  className={`${classes.dropZoneArea} ${
                    isDragOver ? classes.dropZoneActive : classes.dropZoneDefault
                  }`}
                  role="button"
                  tabIndex={0}
                  onClick={openFilePicker}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault()
                      openFilePicker()
                    }
                  }}
                >
                  <div className={classes.dropIconHost}>
                    <span style={{ fontSize: 20 }}>📎</span>
                  </div>
                  <p className={classes.dropText}>
                    <button
                      type="button"
                      className={classes.dropLink}
                      onClick={(e) => { e.stopPropagation(); openFilePicker() }}
                    >
                      Click to upload
                    </button>
                    <span style={{ color: 'var(--ds-fg-tertiary, #8c92a5)' }}> or drag and drop</span>
                  </p>
                  <p className={classes.dropHint}>
                    PDF, XLSX, DOCX, JPG or PNG. Max {formatAttachmentSizeLimit()} per file, up to{' '}
                    {MAX_ATTACHMENTS_PER_MESSAGE} files.
                  </p>
                </div>
              )}
            </AttachmentDropzone>

            {(attachments.length > 0 || Boolean(attachmentError)) && (
              <AttachmentStagingArea
                attachments={attachments}
                error={attachmentError}
                maxRows={8}
                onRemove={removeAttachment}
              />
            )}

            <div className={classes.footer}>
              <p className={classes.statusText}>{uploadStatusText}</p>
              <button
                type="button"
                className={`${classes.submitButton} ${
                  canSubmit ? classes.submitEnabled : classes.submitDisabled
                }`}
                disabled={!canSubmit}
                onClick={handleSubmit}
              >
                Analyze Documents
              </button>
            </div>
          </div>
        </div>

        <div className={classes.infoBox}>
          <div className={classes.infoRow}>
            <div className={classes.infoIcon}>i</div>
            <p className={classes.infoText}>Attachments are managed by this flow.</p>
          </div>
        </div>
      </div>
    </div>
  )
}

// Self-register into the ephemeral surface registry
registerEphemeralSurface('analyze-upload-form', AnalyzeUploadSurface)

export default AnalyzeUploadSurface
